import React, { useState } from 'react';
import { Key, Phone, Smartphone, Pencil, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { Profile } from './types';

interface AccessInfoCardProps {
  profile: Profile;
  onEditPin?: () => void;
  onDelete?: () => Promise<void>;
  canDelete?: boolean;
  isDeleting?: boolean;
  memberType?: 'member' | 'seller';
}

export const AccessInfoCard: React.FC<AccessInfoCardProps> = ({
  profile,
  onEditPin,
  onDelete,
  canDelete = false,
  isDeleting = false,
  memberType = 'member',
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete();
      setIsDialogOpen(false);
    }
  };

  const typeLabel = memberType === 'seller' ? 'vendedor' : 'miembro';

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10 shadow-lg shadow-black/10">
      <h3 className="text-white font-medium mb-4 flex items-center gap-2">
        <Key className="w-5 h-5 text-amber-500" />
        Información de Acceso
      </h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-3">
          <p className="text-white/50 text-xs mb-1">Usuario</p>
          <p className="text-white font-mono">{profile.username || 'No definido'}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-white/50 text-xs">PIN</p>
            {onEditPin && (
              <button
                onClick={onEditPin}
                className="text-amber-400 hover:text-amber-300 transition-colors"
                title="Editar PIN"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <p className="text-white font-mono text-lg tracking-widest">{profile.pin || '----'}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <p className="text-white/50 text-xs mb-1 flex items-center gap-1">
            <Phone className="w-3 h-3" /> Teléfono
          </p>
          <a 
            href={`https://wa.me/${(() => {
              const digits = profile.telefono.replace(/\D/g, '');
              // Prepend 506 if number is 8 digits (Costa Rica)
              return digits.length === 8 ? '506' + digits : digits;
            })()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-400 hover:text-green-300 hover:underline transition-colors flex items-center gap-1"
          >
            {profile.telefono}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <p className="text-white/50 text-xs mb-1 flex items-center gap-1">
            <Smartphone className="w-3 h-3" /> Modelo
          </p>
          <p className="text-white">{profile.modelo_celular}</p>
        </div>
      </div>

      {/* Delete button - only visible to admins */}
      {canDelete && onDelete && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive" 
                size="sm" 
                className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 border border-red-500/30"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {typeLabel}
                  </>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-zinc-900 border-white/10">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">¿Eliminar {typeLabel}?</AlertDialogTitle>
                <AlertDialogDescription className="text-white/60">
                  Esta acción eliminará permanentemente a <span className="font-semibold text-white">{profile.nombre}</span> y todos sus datos asociados (membresía, canjes, compras, pagos). Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Eliminando...' : 'Sí, eliminar'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
};

export default AccessInfoCard;
